import React, { useState } from 'react'
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
  SortingState,
  ColumnFiltersState,
  Row,
} from '@tanstack/react-table'
import { 
  ChevronUpIcon, 
  ChevronDownIcon, 
  ArrowsUpDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/20/solid'
import { OrderStatus } from '@/types'
import { statusColors } from './tables/columns'

const statusOrder: OrderStatus[] = [
  'rfq',
  'quoted',
  'in_draft',
  'pending_confirmation',
  'confirmed',
  'in_production',
  'final_testing',
  'ready_for_shipping',
  'in_delivery',
  'completed',
  'cancelled'
]

interface DataTableProps<TData> {
  data: TData[]
  columns: ColumnDef<TData, any>[]
  title: string
  onRowClick?: (row: TData) => void
  getRowProps?: (row: Row<TData>) => Record<string, any>
  renderSubComponent?: (row: Row<TData>) => React.ReactNode
  getRowId?: (row: TData) => string
}

export function DataTable<TData>({
  data,
  columns,
  title,
  onRowClick,
  getRowProps,
  renderSubComponent,
  getRowId = (row: TData) => (row as any).id,
}: DataTableProps<TData>) {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [sorting, setSorting] = useState<SortingState>([])
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  const toggleRow = (rowId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev)
      if (newSet.has(rowId)) {
        newSet.delete(rowId)
      } else {
        newSet.add(rowId)
      }
      return newSet
    })
  }

  const table = useReactTable({
    data,
    columns,
    state: {
      columnFilters,
      globalFilter,
      sorting,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: (row, filterValue) => {
      const searchValue = filterValue.toLowerCase()
      const order = row.original as any

      // Search in order number
      if (order.orderNumber?.toLowerCase().includes(searchValue)) {
        return true
      }

      // Search in products
      if (order.products?.some((product: any) => 
        product.name?.toLowerCase().includes(searchValue) ||
        product.category?.toLowerCase().includes(searchValue)
      )) {
        return true
      }

      // Search in dates
      const dateFields = [
        order.orderDate,
        order.requestedDeliveryDate,
        order.confirmedDeliveryDate,
        order.revisedDeliveryDate,
        order.actualDeliveryDate
      ]
      
      if (dateFields.some(date => {
        if (!date) return false
        const dateStr = new Date(date).toLocaleDateString()
        return dateStr.includes(searchValue)
      })) {
        return true
      }

      return false
    },
  })

  // Get unique status values for filtering
  const statusColumn = table.getColumn('status')
  const uniqueStatuses = statusColumn
    ? Array.from(new Set(data.map((item: any) => item.status)))
        .filter((status): status is OrderStatus => 
          statusOrder.includes(status as OrderStatus)
        )
    : []

  // Sort statuses according to chronological order
  const statuses = uniqueStatuses.sort((a, b) => 
    statusOrder.indexOf(a) - statusOrder.indexOf(b)
  )

  // Get current status filters
  const statusFilter = columnFilters.find(f => f.id === 'status')
  const selectedStatuses = (statusFilter?.value as OrderStatus[]) || []

  // Calculate status counts
  const statusCounts = data.reduce((acc, item) => {
    const status = (item as any).status as OrderStatus
    if (statusOrder.includes(status)) {
      acc[status] = (acc[status] || 0) + 1
    }
    return acc
  }, {} as Record<OrderStatus, number>)

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {title}
          </h2>
          <div className="relative">
            <input
              type="text"
              placeholder="Search orders..."
              value={globalFilter}
              onChange={e => setGlobalFilter(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-lg text-sm bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>
        {statuses.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {statuses.map(status => (
              <button
                key={status}
                onClick={() => {
                  const newFilters = columnFilters.filter(f => f.id !== 'status')
                  if (!selectedStatuses.includes(status)) {
                    newFilters.push({
                      id: 'status',
                      value: [...selectedStatuses, status],
                    })
                  }
                  setColumnFilters(newFilters)
                }}
                className={`px-2 py-1 rounded-full text-xs ${
                  selectedStatuses.includes(status)
                    ? statusColors[status].selected
                    : statusColors[status].unselected
                }`}
              >
                {status.replace(/_/g, ' ')} ({statusCounts[status] || 0})
              </button>
            ))}
          </div>
        )}
        {(globalFilter || selectedStatuses.length > 0) && (
          <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            Showing {table.getFilteredRowModel().rows.length} of {data.length} orders
            {globalFilter && (
              <span> matching "{globalFilter}"</span>
            )}
            {selectedStatuses.length > 0 && (
              <span> with status{selectedStatuses.length > 1 ? 'es' : ''}: {selectedStatuses.map(s => s.replace(/_/g, ' ')).join(', ')}</span>
            )}
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700">
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        {...{
                          className: header.column.getCanSort()
                            ? 'cursor-pointer select-none flex items-center gap-1'
                            : '',
                          onClick: header.column.getToggleSortingHandler(),
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {header.column.getCanSort() && (
                          <>
                            {{
                              asc: <ChevronUpIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />,
                              desc: <ChevronDownIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />,
                            }[header.column.getIsSorted() as string] ?? (
                              <ArrowsUpDownIcon className="w-4 h-4 text-gray-400 dark:text-gray-600" />
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </th>
                ))}
                <th className="w-12"></th>
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {table.getRowModel().rows.map(row => (
              <React.Fragment key={row.id}>
                <tr
                  {...(getRowProps ? getRowProps(row) : {})}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer group"
                  onClick={() => {
                    const rowId = getRowId(row.original)
                    toggleRow(rowId)
                    onRowClick?.(row.original)
                  }}
                >
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className="px-6 py-4">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                  <td className="px-6 py-4 text-right">
                    <ChevronRightIcon className={`w-5 h-5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-transform duration-200 ${expandedRows.has(getRowId(row.original)) ? 'rotate-90' : ''}`} />
                  </td>
                </tr>
                {renderSubComponent && expandedRows.has(getRowId(row.original)) && (
                  <tr>
                    <td colSpan={row.getVisibleCells().length + 1}>
                      {renderSubComponent(row)}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
              className="p-1 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
            >
              <ChevronDoubleLeftIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="p-1 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="p-1 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
              className="p-1 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
            >
              <ChevronDoubleRightIcon className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300">
              <div>Page</div>
              <strong>
                {table.getState().pagination.pageIndex + 1} of{' '}
                {table.getPageCount()}
              </strong>
            </span>
            <select
              value={table.getState().pagination.pageSize}
              onChange={e => {
                table.setPageSize(Number(e.target.value))
              }}
              className="px-2 py-1 border rounded bg-white dark:bg-gray-700 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm"
            >
              {[5, 10, 15, 20].map(pageSize => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  )
} 